import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { differenceBy, isEqual } from 'lodash';
import {
    REPORT_SETTINGS_PREFIX,
    PRESET_SETTINGS_PREFIX,
} from 'modules/taboola-common-frontend-modules/storage/DBStorage/constant';
import { useDBStorage } from 'modules/taboola-common-frontend-modules/storage/DBStorage/useDBStorage';
import { useDynamicReport } from '../../../../scheduled-reports/hooks/useDynamicReport';
import { addIndication, INDICATION_TYPES } from '../../../../taboola-common-frontend-modules/Indications';
import { FormattedMessage } from '../../../../taboola-common-frontend-modules/i18n';
import { isDynamicReport, isStaticReport } from '../../../config/isStaticDynamicReport';
import {
    getColumnDefsForConfigPreset,
    getMatchedColumnState,
    getMergedColumnStateWithDef,
    hasUnPinnedColumns,
    injectSortModelToColumnState,
    mergeFullStateWithSubState,
} from '../services';
import { isCustomReportPreset } from '../utils';

export const getBasePresetsDBKey = reportId => `${REPORT_SETTINGS_PREFIX}.${reportId}.${PRESET_SETTINGS_PREFIX}`;
export const getPresetDBKey = (reportId, presetName) => `${getBasePresetsDBKey(reportId)}.${presetName}`;
const getColumnStateDBKey = (reportId, presetName) => `${getPresetDBKey(reportId, presetName)}.gridColumnState`;

export const allPinnedIndication = {
    message: (
        <FormattedMessage
            id="app.campaigns.reports.grid.column.all.pinned"
            defaultMessage="It should be at least one unpinned column in the grid."
        />
    ),
    type: INDICATION_TYPES.INFO,
    highlight: <FormattedMessage id="note.highlight" defaultMessage="Note:" />,
};

export const useColumnState = (reportId, presetName, columnDefs) => {
    const dispatch = useDispatch();
    const { getter, setter } = useDBStorage();
    const { getColumnState: getDynamicColumnState, setColumnState: setDynamicColumnState } = useDynamicReport(reportId);
    const getValue = useCallback(
        (reportId, passedPresetName) =>
            isStaticReport(reportId)
                ? getter(getColumnStateDBKey(reportId, passedPresetName))
                : getDynamicColumnState(),
        [getter, getDynamicColumnState]
    );
    const setValue = useCallback(
        (value, passedReportId, passedPresetName, autoUpdate) =>
            setter(getColumnStateDBKey(passedReportId, passedPresetName), value, { autoUpdate }),
        [setter]
    );
    const columnState = getValue(reportId, presetName) || null;

    const saveColumnStateByPresetAndColumnDefs = useCallback(
        ({ reportConfig, reportId, reportPresetName, selectedColumns, reportSorting, columnDefs, autoUpdate }) => {
            if (isDynamicReport(reportId)) {
                return;
            }
            const columnState = getValue(reportId, reportPresetName);
            const allConfigColumnDefs = reportConfig.columnsDef;
            const isCustomPreset = isCustomReportPreset(reportPresetName);

            const resolvedColumnDefs = isCustomPreset
                ? [...differenceBy(allConfigColumnDefs, columnDefs, ({ colId }) => colId), ...columnDefs]
                : getColumnDefsForConfigPreset(allConfigColumnDefs, reportConfig, reportPresetName);

            const newColumnState = getMergedColumnStateWithDef({
                columnState,
                columnDefs: resolvedColumnDefs,
                reportConfig,
                selectedColumns,
                reportPresetName,
            });

            const columnStateAlignedSorting = injectSortModelToColumnState({
                columnState: newColumnState,
                reportSorting,
                columnDefs,
            });

            if (isEqual(columnState, columnStateAlignedSorting)) {
                return;
            }

            setValue(columnStateAlignedSorting, reportId, reportPresetName, autoUpdate);
        },
        [getValue, setValue]
    );

    const saveColumnStateOnGridChange = useCallback(
        ({ columnState, resetPrevSorting, reportId, reportPresetName }) => {
            if (!hasUnPinnedColumns(columnState) && isStaticReport(reportId)) {
                dispatch(addIndication(allPinnedIndication));
            }

            if (isDynamicReport(reportId)) {
                setDynamicColumnState(columnState);
                return;
            }

            const prevColumnState = getValue(reportId, reportPresetName);
            const mergedState = mergeFullStateWithSubState(prevColumnState, columnState, resetPrevSorting);

            setValue(mergedState, reportId, reportPresetName);
        },
        [getValue, setValue, setDynamicColumnState, dispatch]
    );

    const matchedColumnState = useMemo(() => {
        const matchedState = columnDefs ? getMatchedColumnState(columnState, columnDefs) : columnState;

        return matchedState;
    }, [columnState, columnDefs]);

    return { columnState: matchedColumnState, saveColumnStateByPresetAndColumnDefs, saveColumnStateOnGridChange };
};
